'use client';

import type { ModelName } from '@pigello/pigello-matrix';
import { cloneDeep } from 'lodash';
import { Fragment } from 'react';
import { useTableFilter } from '@/hooks/use-table-filter';
import type { QueryParams } from '@/requests/types';
import type { Filter, FilterEnumChoice } from './constants';
import EnumFilter from './enum-filter';
import {
  getFilterIsActive,
  handleAddFilter,
  handleRemoveFilter,
} from './utils';

export default function TableEnumFilters({
  filters,
  isClient,
  tableId,
  modelName,
  overrideUrl,
  queryParamFilters,
  isFetchingTableData,
  enableGlobalFilters,
}: {
  filters: Filter[];
  isClient: boolean;
  tableId: string;
  modelName: ModelName;
  overrideUrl?: string;
  queryParamFilters: QueryParams['filters'];
  isFetchingTableData: boolean;
  enableGlobalFilters?: boolean;
}) {
  const { filter, setTableFilter } = useTableFilter({ isClient, tableId });

  const toggleFilter = (
    filterVariant: Filter,
    choice: FilterEnumChoice,
    remove: boolean
  ) => {
    const filterCopy = cloneDeep(filter ?? {});

    if (remove) {
      handleRemoveFilter({ filterCopy, filterVariant, choice });
    } else {
      handleAddFilter({ filterCopy, filterVariant, choice });
    }

    setTableFilter(filterCopy);
  };

  const filteredFilters = filters.filter(
    (f) => f.enumChoices && f.enumChoices.length > 0
  );

  if (filteredFilters.length === 0) return null;

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {filteredFilters.map((filterVariant) => {
        return (
          <Fragment key={filterVariant.title}>
            {filterVariant.enumChoices!.map((choice) => {
              const isActive = getFilterIsActive(filterVariant, choice, filter);

              if (!isActive) return null;

              return (
                <EnumFilter
                  isFetchingTableData={isFetchingTableData}
                  overrideUrl={overrideUrl}
                  modelName={modelName}
                  key={choice.title}
                  choice={choice}
                  filter={filter}
                  filterVariant={filterVariant}
                  toggleFilter={toggleFilter}
                  queryParamFilters={queryParamFilters}
                  enableGlobalFilters={enableGlobalFilters}
                />
              );
            })}
          </Fragment>
        );
      })}
    </div>
  );
}
